import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

const UPDATE_INTERVAL = 2 * 60 * 1000; // 2 minutes

export const PresenceTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  const lastUpdateRef = useRef<number>(0);
  const lastRouteRef = useRef<string>("");

  useEffect(() => {
    const updatePresence = async () => {
      const now = Date.now();
      const currentRoute = location.pathname;
      
      // Throttle updates - only update if route changed or 2min passed
      const routeChanged = currentRoute !== lastRouteRef.current;
      const timeElapsed = now - lastUpdateRef.current > UPDATE_INTERVAL;
      
      if (!routeChanged && !timeElapsed && lastUpdateRef.current > 0) {
        return;
      }
      
      lastUpdateRef.current = now;
      lastRouteRef.current = currentRoute;

      try {
        // Call edge function for visitor tracking (uses service role, bypasses RLS)
        // This is fire-and-forget, we don't await or check errors to avoid blocking UI
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-visit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ route: currentRoute }),
        }).catch(() => {
          // Silently fail - tracking is non-critical
        });

        // Update user presence if authenticated (this table has proper RLS for auth users)
        if (user?.id) {
          await supabase
            .from("user_presence")
            .upsert(
              {
                user_id: user.id,
                last_seen_at: new Date().toISOString(),
                is_online: true,
                current_route: currentRoute,
              },
              { onConflict: "user_id" }
            );
        }
      } catch (error) {
        // Silently fail - presence tracking is non-critical
      }
    };

    // Update immediately on mount and route change
    updatePresence();

    // Set up interval for periodic updates
    const intervalId = setInterval(updatePresence, UPDATE_INTERVAL);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updatePresence();
      } else if (user?.id) {
        // Set offline when tab is hidden
        supabase
          .from("user_presence")
          .update({ is_online: false })
          .eq("user_id", user.id)
          .then();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      
      // Set offline on unmount if authenticated
      if (user?.id) {
        supabase
          .from("user_presence")
          .update({ is_online: false })
          .eq("user_id", user.id)
          .then();
      }
    };
  }, [location.pathname, user?.id]);

  return null;
};
